import React from 'react';

import LoaderComponent from 'coreComponents/LoaderComponent';
import withSSRPage from 'coreComponents/withSSRPage';
import {useLocation, useServerData, useSSRRedirect, useSSRRequest} from 'coreHooks';
import {useCategoryActions} from 'coreHooks/categories';
import {lazyComponent} from 'coreUtils/adapters/lazy';
import {absoluteUrl, getEncodedURI} from 'coreUtils/url';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import ProductDetailPage from './ProductDetailPage';

const fallback = <LoaderComponent />;
const PageNotFound = lazyComponent(() => import('../../pages/page-not-found'), {fallback});

const PDP_URL_PATTERN = /-(\w+)\.html/;

const ProductDetailRouteContent = () => {
	const {managerRepository} = useGeneralProvider();
	const categoryActions = useCategoryActions();
	const req = useSSRRequest();
	const redirect = useSSRRedirect();
	const location = useLocation();

	const result = useServerData('pdp-route', async () => {
		const match = location.pathname.match(PDP_URL_PATTERN);

		if (!match || match.length <= 1) {
			return {notFound: true, redirected: false};
		}

		const pid = match[1];
		const query = Object.fromEntries(new URLSearchParams(location.search).entries());
		const routeInfoQuery = {...query};
		const additionalQuery = {
			country: managerRepository.getLocalesMgr().getCurrentLocale().isValid()
				? managerRepository.getLocalesMgr().getCurrentCountry().getCountryCode()
				: managerRepository.getLocalesMgr().getDefaultLocale().getCountryCode(),
		};

		let normalizedPath;

		try {
			normalizedPath = managerRepository.getLocalesMgr().normalizeLink(location.pathname);
		} catch (err) {
			console.error(err, {context: 'PDP normalizeLink failure', pathname: location.pathname});
			redirect(301, '/');

			return {redirected: true, notFound: false};
		}

		if (req?.data?.product?.data) {
			managerRepository.getProductsMgr().storeProducts(req.data.product.data);
		}

		const productPromise = managerRepository.getProductsMgr().getProductSync(pid);

		const routeInfoPromise = (req?.data?.routeInfo
			? Promise.resolve(req.data.routeInfo)
			: managerRepository.getSfccAPI().getRouteInfo(getEncodedURI(normalizedPath), {
				...routeInfoQuery,
				...additionalQuery,
			})
		).then((routeDetails) => {
			const metadata = [];
			const languageCode = managerRepository.getLocalesMgr().getCurrentLocale().getLanguageCode();

			if (req?.data?.localizedSeo?.[languageCode]) {
				metadata.push(req.data.localizedSeo[languageCode]);
			} else if (req?.data?.pageCollection?.items?.length > 0 && req.data.pageCollection.items[0].metadata) {
				metadata.push(req.data.pageCollection.items[0].metadata);
			}

			if (routeDetails.metadata) {
				metadata.push(routeDetails.metadata);
			}

			return {metadata, pageUrls: routeDetails.pageUrls, jsonld: routeDetails.jsonld};
		});

		// SEO is part of the cached result (applied by `usePageInit` on every mount), fetched in parallel.
		const [product, routeSeo] = await Promise.all([productPromise, routeInfoPromise]);
		const isBannedA = await product.isBannedA();

		let isRequiresLoginForRegisteredBannedA = false;
		const primaryCategoryId = product?.getPrimaryCategoryId?.();

		if (primaryCategoryId) {
			const primaryCategory = await categoryActions.getCategoryPromise(primaryCategoryId);
			const isRequiresLoginInHierarchy = primaryCategory?.isRequiresLoginInHierarchy?.();
			const availableForCustomerGroupA = product.getAvailableForCustomerGroupA();

			isRequiresLoginForRegisteredBannedA =
				!!availableForCustomerGroupA &&
				availableForCustomerGroupA.includes('Registered') &&
				isBannedA &&
				isRequiresLoginInHierarchy;
		}

		const notFound =
			!product?.getProductId() ||
			product.isOffline() ||
			(isBannedA && !isRequiresLoginForRegisteredBannedA);

		if (notFound) {
			return {notFound: true, redirected: false};
		}

		const variationGroup = product?.isVariationGroup()
			? product
			: await managerRepository.getProductsMgr().getProductSync(product?.getVariationGroupId(), true);

		const productUrl = variationGroup?.getProductUrl();

		return {
			pid,
			variationGroupId: variationGroup?.getProductId(),
			notFound: false,
			redirected: false,
			isRequiresLoginForRegisteredBannedA,
			seo: {...routeSeo, canonical: absoluteUrl(productUrl || location.pathname)},
		};
	}, [location.pathname]);

	if (result.redirected) {
		return null;
	}

	if (result.notFound) {
		return <PageNotFound />;
	}

	return (
		<ProductDetailPage
			pid={result.pid}
			variationGroupId={result.variationGroupId}
			isRequiresLoginForRegisteredBannedA={result.isRequiresLoginForRegisteredBannedA}
			seo={result.seo}
		/>
	);
};

export default withSSRPage(ProductDetailRouteContent, 'product-detail');
